import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { ArrowLeft, FloppyDisk } from '@phosphor-icons/react';
import VolunteersPanel from './VolunteersPanel';
import ProbesPanel from './ProbesPanel';
import StudyDashboard from './StudyDashboard';
import ScheduleCalendar from './ScheduleCalendar';
import QueriesPanel from './QueriesPanel';
import MonitoringMatrixPanel from '../components/study/monitoring/MonitoringMatrixPanel';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '../components/ui/alert-dialog';
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem } from '../components/ui/command';
import { toast } from 'sonner';
import { errText } from '../utils/errors';
import { isoWeekLabel } from '../utils/isoWeek';
import { statusOptions, productTypes } from '../components/study/studyConstants';
import { useStatuses } from '../hooks/useStatuses';
import { ProtocolPanel } from '../components/study/ProtocolPanel';
import { CostesPanel } from '../components/study/CostesPanel';
import { VisitsPanel } from '../components/study/VisitsPanel';
import ScheduleBlocksPanel from '../components/study/ScheduleBlocksPanel';
import ReportsPanel from '../components/study/ReportsPanel';
import StudyConsentsPanel from '../components/study/StudyConsentsPanel';
import DelegationLogPanel from '../components/study/DelegationLogPanel';
import OdontogramaPanel from '../components/study/OdontogramaPanel';
import { QuestionnairesPanel, SamplesPanel, StaffPanel } from '../components/study/StudyPanels';
import { usePermissions } from '../contexts/PermissionsContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StudyForm() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const { canStudy, loadStudyPerms, isAdmin } = usePermissions();
  const isEdit = !!id;
  const urlTab = searchParams.get('tab');
  const urlVolunteerId = searchParams.get('volunteer_id');
  const urlQueryId = searchParams.get('query_id');
  const urlVolunteersTab = searchParams.get('volunteers_tab');
  const urlHighlightVolunteer = searchParams.get('highlight_volunteer');

  const [clients, setClients] = useState([]);
  // URS-F486: estados configurables por el admin (fetch dinamico). Al crear
  // un estudio nuevo se precarga el default marcado en Config -> Estudios ->
  // Estado Estudio; si aun no cargo, cae al legacy 'activo'.
  const { statuses: studyStatuses, defaultKey: studyDefaultStatus } = useStatuses('study');
  const [form, setForm] = useState({
    code: '', title: '', description: '', client_id: '', product_type: 'cosmetico',
    phase: '', status: 'activo', start_date: '', end_date: '', color: '#1f3e80',
    staff: {}, has_images: false, formato: 'presencial',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [initialStatus, setInitialStatus] = useState('activo');
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [incluidosCount, setIncluidosCount] = useState(null);
  const [cancelImpact, setCancelImpact] = useState(null);
  // URS-F356/F357: mapeo de claves de pestañas legadas que ahora son sub-pestañas
  // de otras. Si la URL trae `?tab=questionnaires|samples|delegation`, redirigimos
  // a su nueva pestaña padre.
  const LEGACY_TAB_MAP = { questionnaires: 'visits', samples: 'probes', delegation: 'staff' };
  const normalizeTab = (t) => (t && LEGACY_TAB_MAP[t]) || t;
  const [activeTab, setActiveTab] = useState(normalizeTab(urlTab) || (isEdit ? "dashboard" : "general"));

  // URS-F288: cuando el usuario cambia de pestana, persistir en la URL (?tab=...)
  // para que F5 recargue en la misma pestana en lugar de volver al dashboard.
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('tab', newTab);
      // Limpia params especificos de navegacion cruzada al cambiar manualmente
      next.delete('volunteer_id');
      next.delete('query_id');
      next.delete('volunteers_tab');
      next.delete('highlight_volunteer');
      return next;
    }, { replace: true });
  };

  // Sync active tab when URL query param changes (e.g. clicking a query from Study Dashboard)
  useEffect(() => {
    if (urlTab) setActiveTab(normalizeTab(urlTab));
  }, [urlTab]);

  // URS-F332: cargar permisos por seccion del estudio para ocultar pestanas
  // que el usuario no puede ver (`sondas:view`, `muestras:view`, etc.).
  const [permsLoaded, setPermsLoaded] = useState(false);
  useEffect(() => {
    if (!isEdit || !id) { setPermsLoaded(true); return; }
    loadStudyPerms(id).finally(() => setPermsLoaded(true));
  }, [id, isEdit, loadStudyPerms]);

  // Helper: el usuario puede ver una seccion del estudio.
  // - Si todavia se estan cargando los permisos: si es admin, true; si no, false (evita parpadeo y filtraciones).
  // - En modo "nuevo estudio" (sin id), mostramos todas las pestanas habilitadas por defecto.
  const canSee = (section) => {
    if (!isEdit) return true;
    if (isAdmin) return true;
    if (!permsLoaded) return false;
    return canStudy(id, section, 'view');
  };

  // El boton global "Guardar" solo persiste Datos Generales (pestana general)
  // o Personal (pestana staff). En el resto de pestañas cada panel guarda por
  // su cuenta, asi que lo ocultamos para no provocar un PUT 403 indebido.
  const canEditGeneral = !isEdit || isAdmin || (permsLoaded && canStudy(id, 'datos_generales', 'edit'));
  const canEditStaff = isAdmin || (permsLoaded && canStudy(id, 'personal', 'edit'));
  const showSaveButton = !isEdit
    ? true
    : (activeTab === 'general' && canEditGeneral) || (activeTab === 'staff' && canEditStaff);

  // URS-F375: detectar si el estudio es dental para mostrar la pestaña Odontograma.
  const isDentalStudy = (
    ((form.product_type === 'cosmetico' || form.product_type === 'cosmetico_plus') && form.phase === 'Dental') ||
    (form.product_type === 'sanitario' && form.phase === 'Odontologia')
  );

  const labels = lang === 'es'
    ? { new: 'Nuevo Estudio', edit: 'Editar Estudio', code: 'Codigo del estudio', title: 'Titulo', description: 'Descripcion', client: 'Cliente', product: 'Tipo de producto', phase: 'Fase', status: 'Estado', formato: 'Formato', formato_presencial: 'Presencial', formato_online: 'Online', formato_mixto: 'Mixto', start: 'Fecha inicio', end: 'Fecha fin prevista', save: 'Guardar', select_client: 'Seleccionar cliente...', no_client: 'Sin cliente asignado', created: 'Estudio creado', updated: 'Estudio actualizado', code_required: 'El codigo es obligatorio', title_required: 'El titulo es obligatorio', general: 'Datos generales', planning: 'Planificacion', visits: 'Visitas', questionnaires: 'Cuestionarios', volunteers: 'Voluntarios', samples: 'Muestras', protocol: 'Protocolo', costes: 'Costes', probes: 'Sondas', staff: 'Personal', dashboard: 'Dashboard', queries: 'Queries', reports: 'Informes', consents: 'Consentimientos', delegation: 'Delegacion', has_images: 'El estudio recoge imagenes (activa el modulo de Imagenes en el CI)' }
    : { new: 'New Study', edit: 'Edit Study', code: 'Study code', title: 'Title', description: 'Description', client: 'Client', product: 'Product type', phase: 'Phase', status: 'Status', formato: 'Format', formato_presencial: 'On-site', formato_online: 'Online', formato_mixto: 'Mixed', start: 'Start date', end: 'Expected end date', save: 'Save', select_client: 'Select client...', no_client: 'No client assigned', created: 'Study created', updated: 'Study updated', code_required: 'Code is required', title_required: 'Title is required', general: 'General data', planning: 'Planning', visits: 'Visits', questionnaires: 'Questionnaires', volunteers: 'Volunteers', samples: 'Samples', protocol: 'Protocol', costes: 'Costs', probes: 'Probes', staff: 'Staff', dashboard: 'Dashboard', queries: 'Queries', reports: 'Reports', consents: 'Informed consents', delegation: 'Delegation', has_images: 'Study collects images (enables Images section in CI)' };

  useEffect(() => {
    const load = async () => {
      try {
        const { data: clRaw } = await axios.get(`${API_URL}/api/clients?all=true`, { withCredentials: true });
        const cl = Array.isArray(clRaw) ? clRaw : clRaw.data || [];
        setClients(cl.filter(c => c.status === 'active'));
        if (isEdit) {
          const { data: study } = await axios.get(`${API_URL}/api/studies/${id}`, { withCredentials: true });
          setForm({
            code: study.code || '', title: study.title || '', description: study.description || '',
            client_id: study.client_id || '', product_type: study.product_type || 'cosmetico',
            phase: study.phase || '', status: study.status || 'activo',
            start_date: study.start_date || '', end_date: study.end_date || '',
            color: study.color || '#1f3e80',
            staff: study.staff || {},
            has_images: !!study.has_images,
            formato: study.formato || 'presencial',
          });
          setInitialStatus(study.status || 'activo');
        }
      } catch { toast.error('Error loading data'); }
      setLoading(false);
    };
    load();
  }, [id, isEdit]);

  // URS-F486: cuando cargue la config de estados y el estudio sea NUEVO,
  // aplicar el default configurado por el admin.
  useEffect(() => {
    if (isEdit) return;
    if (!studyDefaultStatus) return;
    setForm(p => (p.status === 'activo' || !p.status ? { ...p, status: studyDefaultStatus } : p));
  }, [isEdit, studyDefaultStatus]);

  const isFinalizedStatus = (s) => ['finalizado', 'finalized'].includes((s || '').toLowerCase());

  const doSave = async () => {
    setSaving(true);
    try {
      if (!isEdit) {
        const { data } = await axios.post(`${API_URL}/api/studies`, form, { withCredentials: true });
        toast.success(labels.created);
        navigate(`/studies/${data.study_id}`);
      } else if (activeTab === 'staff') {
        // Guardar SOLO el personal (requiere permiso 'personal:edit').
        await axios.put(`${API_URL}/api/studies/${id}/staff`, { staff: form.staff }, { withCredentials: true });
        toast.success(labels.updated);
      } else {
        const { data } = await axios.put(`${API_URL}/api/studies/${id}`, form, { withCredentials: true });
        const n = data?.volunteers_completed || 0;
        const cc = data?.cancel_cleanup;
        if (cc) {
          toast.success(`${labels.updated}. ${cc.schedule_slots_deleted} bloque${cc.schedule_slots_deleted === 1 ? '' : 's'} de programación eliminado${cc.schedule_slots_deleted === 1 ? '' : 's'} y ${cc.appointments_cancelled} cita${cc.appointments_cancelled === 1 ? '' : 's'} anulada${cc.appointments_cancelled === 1 ? '' : 's'}.`);
        } else if (n > 0) {
          toast.success(`${labels.updated}. ${n} ${n === 1 ? 'voluntario marcado' : 'voluntarios marcados'} como completado${n === 1 ? '' : 's'}.`);
        } else {
          toast.success(labels.updated);
        }
        setInitialStatus(form.status);
      }
    } catch (err) { toast.error(errText(err)); }
    setSaving(false);
  };

  const handleSave = async () => {
    // El personal se guarda por su propio endpoint; no exige code/title.
    if (!(isEdit && activeTab === 'staff')) {
      if (!form.code.trim()) { toast.error(labels.code_required); return; }
      if (!form.title.trim()) { toast.error(labels.title_required); return; }
    }
    // URS-F516d: al finalizar un estudio, confirmar la cascada incluido -> completado.
    const finalizing = isEdit && activeTab !== 'staff' && isFinalizedStatus(form.status) && !isFinalizedStatus(initialStatus);
    if (finalizing) {
      try {
        const { data } = await axios.get(`${API_URL}/api/studies/${id}/volunteers`, { withCredentials: true });
        const list = Array.isArray(data) ? data : (data?.data || []);
        setIncluidosCount(list.filter(v => v.status === 'incluido').length);
      } catch { setIncluidosCount(null); }
      setFinalizeOpen(true);
      return;
    }
    const cancelling = isEdit && activeTab !== 'staff' && (form.status || '').toLowerCase() === 'cancelado' && (initialStatus || '').toLowerCase() !== 'cancelado';
    if (cancelling) {
      try {
        const { data } = await axios.get(`${API_URL}/api/studies/${id}/cancel-impact`, { withCredentials: true });
        setCancelImpact(data);
      } catch { setCancelImpact({ schedule_slots: null, appointments: null }); }
      return;
    }
    doSave();
  };

  const setField = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  // Si la pestana activa ya no es accesible (porque el admin acaba de
  // restringir permisos), bascula a la primera permitida.
  useEffect(() => {
    if (!permsLoaded || isAdmin) return;
    const tabToSection = {
      dashboard: 'dashboard_estudio',
      general: 'datos_generales',
      staff: 'personal',
      protocol: 'protocolo',
      costes: 'costes',
      visits: 'visitas',
      volunteers: 'voluntarios',
      questionnaires: 'cuestionarios',
      probes: 'sondas',
      samples: 'muestras',
      consents: 'consentimientos',
      queries: 'queries',
      monitoring: 'queries',
      reports: 'informes',
      delegation: 'delegacion',
    };
    const sec = tabToSection[activeTab];
    if (sec && !canStudy(id, sec, 'view')) {
      const fallback = Object.entries(tabToSection).find(([_, s]) => canStudy(id, s, 'view'));
      if (fallback) handleTabChange(fallback[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permsLoaded, activeTab, id, isAdmin]);

  if (loading) return <div className="p-12 text-center text-stone-500">{lang === 'es' ? 'Cargando...' : 'Loading...'}</div>;

  return (
    <div className="space-y-6" data-testid="study-form">
      <AlertDialog open={finalizeOpen} onOpenChange={setFinalizeOpen}>
        <AlertDialogContent data-testid="finalize-study-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Finalizar estudio</AlertDialogTitle>
            <AlertDialogDescription>
              Al marcar el estudio como <strong>Finalizado</strong>,{' '}
              {incluidosCount != null
                ? <>los <strong>{incluidosCount}</strong> voluntario{incluidosCount === 1 ? '' : 's'} en estado «incluido»</>
                : <>todos los voluntarios en estado «incluido»</>}{' '}
              pasarán automáticamente a <strong>completado</strong>. La acción queda registrada en auditoría. ¿Deseas continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="finalize-cancel-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              data-testid="finalize-confirm-btn"
              className="bg-[#1f3e80] hover:bg-[#2a4f8a]"
              onClick={() => { setFinalizeOpen(false); doSave(); }}>
              Finalizar y completar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!cancelImpact} onOpenChange={(o) => { if (!o) setCancelImpact(null); }}>
        <AlertDialogContent data-testid="cancel-study-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar estudio</AlertDialogTitle>
            <AlertDialogDescription data-testid="cancel-study-impact">
              Al marcar el estudio como <strong>Cancelado</strong> se eliminarán <strong>{cancelImpact?.schedule_slots ?? 'todos los'}</strong> bloque{cancelImpact?.schedule_slots === 1 ? '' : 's'} de programación de visitas (también los de fechas pasadas) y se anularán <strong>{cancelImpact?.appointments ?? 'las'}</strong> cita{cancelImpact?.appointments === 1 ? '' : 's'} futura{cancelImpact?.appointments === 1 ? '' : 's'} en estado Citada o Confirmada, con el motivo «Estudio cancelado». No se restaurará nada si el estudio vuelve a otro estado. La acción queda registrada en auditoría. ¿Continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-study-abort-btn">Volver</AlertDialogCancel>
            <AlertDialogAction data-testid="cancel-study-confirm-btn" className="bg-red-600 hover:bg-red-700"
              onClick={() => { setCancelImpact(null); doSave(); }}>
              Cancelar estudio y limpiar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" data-testid="back-to-studies-btn" onClick={() => navigate('/studies')} className="h-8 w-8 text-stone-500 hover:text-stone-900">
            <ArrowLeft size={18} />
          </Button>
          <div>
            <h1 className="text-2xl font-heading font-semibold text-stone-900">
              {isEdit ? labels.edit : labels.new}
            </h1>
            {isEdit && <p className="text-xs font-mono text-stone-400 mt-0.5">{form.code || id}</p>}
          </div>
        </div>
        {showSaveButton && (
          <Button data-testid="save-study-btn" onClick={handleSave} disabled={saving} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-2">
            <FloppyDisk size={16} />
            {saving ? '...' : labels.save}
          </Button>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="bg-stone-100">
          {isEdit && canSee('dashboard_estudio') && <TabsTrigger value="dashboard" data-testid="tab-dashboard">{labels.dashboard}</TabsTrigger>}
          {canSee('datos_generales') && <TabsTrigger value="general" data-testid="tab-general">{labels.general}</TabsTrigger>}
          {canSee('personal') && <TabsTrigger value="staff" data-testid="tab-staff">{labels.staff}</TabsTrigger>}
          {canSee('protocolo') && <TabsTrigger value="protocol" data-testid="tab-protocol">{labels.protocol}</TabsTrigger>}
          {isEdit && canSee('costes') && <TabsTrigger value="costes" data-testid="tab-costes">{labels.costes}</TabsTrigger>}
          {(canSee('visitas') || canSee('cuestionarios')) && <TabsTrigger value="visits" data-testid="tab-visits">{labels.visits}</TabsTrigger>}
          {canSee('voluntarios') && <TabsTrigger value="volunteers" data-testid="tab-volunteers">{labels.volunteers}</TabsTrigger>}
          {/* URS-F357: Cuestionarios pasa a ser sub-pestaña de Visitas. Sondas absorbe Muestras como sub-pestaña. */}
          {(canSee('sondas') || canSee('muestras')) && <TabsTrigger value="probes" data-testid="tab-probes">{labels.probes}</TabsTrigger>}
          {isEdit && canSee('consentimientos') && <TabsTrigger value="consents" data-testid="tab-consents">{labels.consents}</TabsTrigger>}
          {isEdit && canSee('queries') && <TabsTrigger value="queries" data-testid="tab-queries">{labels.queries}</TabsTrigger>}
          {isEdit && canSee('queries') && <TabsTrigger value="monitoring" data-testid="tab-monitoring">{lang === 'es' ? 'Monitorización' : 'Monitoring'}</TabsTrigger>}
          {isEdit && isDentalStudy && canSee('dental') && <TabsTrigger value="dental" data-testid="tab-dental">{lang === 'es' ? 'Odontograma' : 'Dental Chart'}</TabsTrigger>}
          {isEdit && canSee('informes') && <TabsTrigger value="reports" data-testid="tab-reports">{labels.reports}</TabsTrigger>}
        </TabsList>

        {isEdit && (
          <TabsContent value="dashboard" className="mt-6">
            <StudyDashboard studyId={id} />
          </TabsContent>
        )}

        <TabsContent value="protocol" className="mt-6">
          <ProtocolPanel studyId={isEdit ? id : null} lang={lang} />
        </TabsContent>

        {isEdit && (
          <TabsContent value="costes" className="mt-6">
            <CostesPanel studyId={id} lang={lang} />
          </TabsContent>
        )}

        <TabsContent value="staff" className="mt-6">
          {/* URS-F356: Delegación pasa a ser una sub-pestaña dentro de Personal. */}
          {isEdit && canSee('delegacion') ? (
            <Tabs defaultValue="personal-info" className="w-full">
              <TabsList className="bg-stone-100">
                <TabsTrigger value="personal-info" data-testid="staff-subtab-personal">{labels.staff}</TabsTrigger>
                <TabsTrigger value="delegation" data-testid="staff-subtab-delegation">{labels.delegation}</TabsTrigger>
              </TabsList>
              <TabsContent value="personal-info" className="mt-4">
                <StaffPanel staff={form.staff} onChange={staff => setField('staff', staff)} lang={lang} studyId={isEdit ? id : null} isDentalStudy={isDentalStudy} />
              </TabsContent>
              <TabsContent value="delegation" className="mt-4">
                <DelegationLogPanel studyId={id} studyCode={form.code} lang={lang} />
              </TabsContent>
            </Tabs>
          ) : (
            <StaffPanel staff={form.staff} onChange={staff => setField('staff', staff)} lang={lang} studyId={isEdit ? id : null} isDentalStudy={isDentalStudy} />
          )}
        </TabsContent>

        <TabsContent value="general" className="mt-6">
          <div className="bg-white border border-stone-200 rounded-sm p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-stone-800">{labels.code} <span className="text-red-500">*</span></Label>
                <Input data-testid="study-code-input" value={form.code} onChange={e => setField('code', e.target.value.toUpperCase())} placeholder="COS-2026-001" className="bg-white border-stone-200 font-mono" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-stone-800">{labels.status}</Label>
                <Select value={form.status} onValueChange={v => setField('status', v)}>
                  <SelectTrigger data-testid="study-status-select" className="bg-white border-stone-200"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(studyStatuses && studyStatuses.length > 0 ? studyStatuses : statusOptions.map(s => ({ key: s.value, label_es: s.es, label_en: s.en, color: '#5b6473' }))).map(s => (
                      <SelectItem key={s.key} value={s.key}>
                        <span className="inline-flex items-center gap-2">
                          <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
                          {lang === 'es' ? s.label_es : s.label_en}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-stone-800">{labels.formato}</Label>
                <Select value={form.formato || 'presencial'} onValueChange={v => setField('formato', v)}>
                  <SelectTrigger data-testid="study-formato-select" className="bg-white border-stone-200"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="presencial" data-testid="study-formato-presencial">{labels.formato_presencial}</SelectItem>
                    <SelectItem value="online" data-testid="study-formato-online">{labels.formato_online}</SelectItem>
                    <SelectItem value="mixto" data-testid="study-formato-mixto">{labels.formato_mixto}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 md:col-span-3">
                <Label className="text-sm font-medium text-stone-800">{labels.title} <span className="text-red-500">*</span></Label>
                <Input data-testid="study-title-input" value={form.title} onChange={e => setField('title', e.target.value)} className="bg-white border-stone-200" />
              </div>
              <div className="space-y-1.5 md:col-span-3">
                <Label className="text-sm font-medium text-stone-800">{labels.description}</Label>
                <Textarea data-testid="study-description-input" value={form.description} onChange={e => setField('description', e.target.value)} className="bg-white border-stone-200 min-h-[80px]" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-stone-800">{labels.client}</Label>
                <ClientCombobox
                  clients={clients}
                  value={form.client_id}
                  onChange={v => setField('client_id', v)}
                  placeholder={labels.select_client}
                  noClient={labels.no_client}
                  lang={lang}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-stone-800">{labels.product}</Label>
                <Select value={form.product_type} onValueChange={v => { setField('product_type', v); setField('phase', ''); }}>
                  <SelectTrigger data-testid="study-product-select" className="bg-white border-stone-200"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {productTypes.map(p => (
                      <SelectItem key={p.value} value={p.value}>{lang === 'es' ? p.es : p.en}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-stone-800">
                  {form.product_type === 'medicamento' ? labels.phase
                    : form.product_type === 'suplemento' ? labels.phase
                    : (lang === 'es' ? 'Subtipo' : 'Subtype')}
                </Label>
                {(form.product_type === 'cosmetico' || form.product_type === 'cosmetico_plus') ? (
                  <Select value={form.phase || '_none'} onValueChange={v => setField('phase', v === '_none' ? '' : v)}>
                    <SelectTrigger data-testid="study-phase-input" className="bg-white border-stone-200"><SelectValue placeholder={lang === 'es' ? 'Seleccionar subtipo...' : 'Select subtype...'} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">
                        <span className="italic text-stone-500">{lang === 'es' ? 'Sin subtipo' : 'No subtype'}</span>
                      </SelectItem>
                      {['Capilar', 'Corporal', 'Dental', 'Facial', 'Ocular'].map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (form.product_type === 'sanitario') ? (
                  <Select value={form.phase || '_none'} onValueChange={v => setField('phase', v === '_none' ? '' : v)}>
                    <SelectTrigger data-testid="study-phase-input" className="bg-white border-stone-200"><SelectValue placeholder={lang === 'es' ? 'Seleccionar subtipo...' : 'Select subtype...'} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">
                        <span className="italic text-stone-500">{lang === 'es' ? 'Sin subtipo' : 'No subtype'}</span>
                      </SelectItem>
                      {['Ginecologia', 'Med. Estetica', 'Odontologia', 'Oftalmologo', 'Otorrino', 'Tricologia', 'Urologia'].map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input data-testid="study-phase-input" value={form.phase} onChange={e => setField('phase', e.target.value)} placeholder="Fase I" className="bg-white border-stone-200" />
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-stone-800">{lang === 'es' ? 'Color del estudio' : 'Study color'}</Label>
                <div className="flex items-center gap-2">
                  <input data-testid="study-color-input" type="color" value={form.color} onChange={e => setField('color', e.target.value)} className="w-10 h-9 rounded-sm border border-stone-200 cursor-pointer" />
                  <span className="text-xs font-mono text-stone-500">{form.color}</span>
                </div>
              </div>
              <div className="space-y-1.5 md:col-span-3">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!form.has_images}
                    onChange={e => setField('has_images', e.target.checked)}
                    className="mt-1"
                    data-testid="study-has-images-checkbox"
                  />
                  <span className="text-sm text-stone-700">{labels.has_images}</span>
                </label>
              </div>
            </div>
          </div>
          <div className="bg-white border border-stone-200 rounded-sm p-6 mt-4">
            <h2 className="text-base font-heading font-medium text-stone-800 mb-5">{labels.planning}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-stone-800">{labels.start}</Label>
                <Input data-testid="study-start-date" type="date" value={form.start_date} onChange={e => setField('start_date', e.target.value)} className="bg-white border-stone-200" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-stone-800">{lang === 'es' ? 'Semana Inicio' : 'Start Week'}</Label>
                <div className="h-10 flex items-center px-3 bg-stone-50 border border-stone-200 rounded-md text-sm font-mono text-stone-700" data-testid="study-week-number">
                  {/* URS-F504: calculo ISO 8601 correcto. Antes se usaba
                      Math.ceil((days+jan1.getDay()+1)/7) que no respeta
                      ISO (2027-01-01 vie devolvia S1 en vez de S53/2026). */}
                  {form.start_date ? (isoWeekLabel(form.start_date) || '—') : '—'}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-stone-800">{labels.end}</Label>
                <Input data-testid="study-end-date" type="date" value={form.end_date} onChange={e => setField('end_date', e.target.value)} className="bg-white border-stone-200" />
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="visits" className="mt-6">
          <Tabs defaultValue={canSee('visitas') ? 'visits-list' : 'questionnaires'} className="w-full">
            <TabsList className="bg-stone-100 mb-4">
              {canSee('visitas') && <TabsTrigger value="visits-list" data-testid="subtab-visits-list">{lang === 'es' ? 'Visitas' : 'Visits'}</TabsTrigger>}
              {canSee('visitas') && <TabsTrigger value="schedule" data-testid="subtab-schedule">{lang === 'es' ? 'Programacion' : 'Scheduling'}</TabsTrigger>}
              {canSee('visitas') && <TabsTrigger value="blocks" data-testid="subtab-blocks">{lang === 'es' ? 'Bloqueos' : 'Blocks'}</TabsTrigger>}
              {canSee('cuestionarios') && <TabsTrigger value="questionnaires" data-testid="subtab-questionnaires">{labels.questionnaires}</TabsTrigger>}
            </TabsList>
            {canSee('visitas') && (
              <TabsContent value="visits-list">
                <VisitsPanel studyId={isEdit ? id : null} lang={lang} />
              </TabsContent>
            )}
            {canSee('visitas') && (
              <TabsContent value="schedule">
                <ScheduleCalendar studyId={isEdit ? id : null} studyColor={form.color || '#1f3e80'} lang={lang} />
              </TabsContent>
            )}
            {canSee('visitas') && (
              <TabsContent value="blocks">
                <ScheduleBlocksPanel studyId={isEdit ? id : null} studyColor={form.color || '#1f3e80'} lang={lang} />
              </TabsContent>
            )}
            {canSee('cuestionarios') && (
              <TabsContent value="questionnaires">
                <QuestionnairesPanel studyId={isEdit ? id : null} lang={lang} />
              </TabsContent>
            )}
          </Tabs>
        </TabsContent>

        <TabsContent value="volunteers" className="mt-6">
          <VolunteersPanel
            studyId={isEdit ? id : null}
            lang={lang}
            studyPhase={form.phase || ''}
            autoExpandVolunteerId={urlTab === 'volunteers' ? urlVolunteerId : null}
            forcedSubTab={urlTab === 'volunteers' ? urlVolunteersTab : null}
            highlightVolunteerId={urlTab === 'volunteers' ? urlHighlightVolunteer : null}
            openQuestionnaireKey={urlTab === 'volunteers' ? searchParams.get('open_q') : null}
            openQueryId={urlTab === 'volunteers' ? urlQueryId : null}
          />
        </TabsContent>

        <TabsContent value="probes" className="mt-6">
          {/* URS-F357: Muestras pasa a ser sub-pestaña de Sondas. */}
          {canSee('muestras') ? (
            <Tabs defaultValue={canSee('sondas') ? 'probes-list' : 'samples'} className="w-full">
              <TabsList className="bg-stone-100 mb-4">
                {canSee('sondas') && <TabsTrigger value="probes-list" data-testid="subtab-probes-list">{labels.probes}</TabsTrigger>}
                <TabsTrigger value="samples" data-testid="subtab-samples">{labels.samples}</TabsTrigger>
              </TabsList>
              {canSee('sondas') && (
                <TabsContent value="probes-list">
                  <ProbesPanel studyId={isEdit ? id : null} studyTitle={form.title} lang={lang} />
                </TabsContent>
              )}
              <TabsContent value="samples">
                <SamplesPanel studyId={isEdit ? id : null} lang={lang} />
              </TabsContent>
            </Tabs>
          ) : (
            <ProbesPanel studyId={isEdit ? id : null} studyTitle={form.title} lang={lang} />
          )}
        </TabsContent>
        {isEdit && (
          <TabsContent value="consents" className="mt-6">
            <StudyConsentsPanel studyId={id} studyHasImages={!!form.has_images} lang={lang} highlightVolunteerId={urlTab === 'consents' ? urlHighlightVolunteer : null} />
          </TabsContent>
        )}
        {isEdit && (
          <TabsContent value="queries" className="mt-6">
            <QueriesPanel studyId={id} lang={lang} autoOpenQueryId={urlTab === 'queries' ? urlQueryId : null} />
          </TabsContent>
        )}
        {isEdit && canSee('queries') && (
          <TabsContent value="monitoring" className="mt-6">
            {activeTab === 'monitoring' && <MonitoringMatrixPanel studyId={id} lang={lang} />}
          </TabsContent>
        )}
        {isEdit && isDentalStudy && (
          <TabsContent value="dental" className="mt-6">
            <OdontogramaPanel studyId={id} canEdit={isAdmin || canStudy(id, 'dental', 'edit')} isAdmin={isAdmin} />
          </TabsContent>
        )}
        {isEdit && (
          <TabsContent value="reports" className="mt-6">
            <ReportsPanel studyId={id} studyCode={form.code} lang={lang} isDentalStudy={isDentalStudy} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}


function ClientCombobox({ clients, value, onChange, placeholder, noClient, lang }) {
  const [open, setOpen] = useState(false);
  const selected = clients.find(c => c.client_id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid="study-client-select"
          className="w-full justify-between font-normal text-left h-9 bg-white border-stone-200"
        >
          <span className="truncate">
            {selected ? `${selected.name} (${selected.client_id})` : placeholder}
          </span>
          <svg width="12" height="12" viewBox="0 0 12 12" className="ml-2 shrink-0 opacity-50"><path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" /></svg>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput placeholder={lang === 'es' ? 'Buscar cliente...' : 'Search client...'} data-testid="study-client-search" />
          <CommandList>
            <CommandEmpty>{lang === 'es' ? 'Sin resultados' : 'No results'}</CommandEmpty>
            <CommandItem
              value="_none_sin_cliente"
              onSelect={() => { onChange(''); setOpen(false); }}
            >
              <span className="text-xs text-stone-400">{noClient}</span>
            </CommandItem>
            {clients.map(c => (
              <CommandItem
                key={c.client_id}
                value={`${c.name} ${c.client_id}`}
                onSelect={() => { onChange(c.client_id); setOpen(false); }}
                data-testid={`study-client-option-${c.client_id}`}
              >
                <span className="truncate text-xs">{c.name} ({c.client_id})</span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
